/**
 * Analytics Portal V1 — the Business metric map (Phase 12, plan §4.4).
 *
 * Every entry names a key the Phase 8 `GET /api/analytics/business` payload may contain. Nothing
 * here computes or derives a value, and a card renders only when the server returned that key — so
 * Business Share, Business Copy Link, intro-video plays, pitch-deck metrics, product/service
 * performance and revenue attribution can never appear (C4). `search_ctr` is present in the payload
 * but deliberately not rendered in V1 (plan §12.12 A11-2).
 *
 * Contact and internal-link privacy: the payload exposes only the aggregates `outbound_clicks` and
 * `internal_clicks` with no per-link-kind split, so the dashboard shows those aggregates honestly
 * and never an address, phone number, URL, social handle, destination id or the identity of whoever
 * clicked (plan §12.13 A12-2).
 */
export const BUSINESS_HEADLINE = [
  { key: "visits", label: "Business profile visits", hint: "One visit per session." },
  { key: "unique_visitors", label: "Unique visitors" },
  { key: "returning_visitors", label: "Returning visitors" },
  { key: "engagement_rate", label: "Engagement rate", suffix: "%" },
];

export const BUSINESS_GROUPS = [
  {
    id: "discovery",
    title: "Discovery",
    description: "How people came across this business.",
    metrics: [
      { key: "search_impressions", label: "Search impressions" },
      { key: "search_clicks", label: "Search clicks" },
      { key: "unique_search_clickers", label: "Unique search clickers" },
      { key: "directory_impressions", label: "Directory impressions" },
      { key: "directory_clicks", label: "Directory clicks" },
      { key: "unique_directory_clickers", label: "Unique directory clickers" },
    ],
  },
  {
    id: "followers",
    title: "Followers & fans",
    description: "Current totals are live; losses and net change are measured from instrumentation.",
    metrics: [
      { key: "followers_current", label: "Followers now" },
      { key: "followers_added", label: "Followers gained" },
      { key: "followers_lost", label: "Followers lost" },
      { key: "followers_net", label: "Net followers" },
      { key: "fans_current", label: "Fans now" },
      { key: "fans_added", label: "Fans gained" },
      { key: "fans_lost", label: "Fans lost" },
      { key: "fans_net", label: "Net fans" },
    ],
  },
  {
    id: "bookmarks",
    title: "Bookmarks",
    metrics: [
      { key: "bookmarks_current", label: "Bookmarks now" },
      { key: "bookmarks_added", label: "Bookmarks added" },
      { key: "bookmarks_removed", label: "Bookmarks removed" },
      { key: "bookmarks_net", label: "Net bookmarks" },
    ],
  },
  {
    id: "interest",
    title: "Questions & meetings",
    description: "Counts and metadata only — never question, answer or message content. "
      + "A meeting request is the primary conversion for a business profile.",
    metrics: [
      { key: "questions", label: "Questions received" },
      { key: "questions_answered", label: "Questions answered" },
      { key: "meeting_requests", label: "Meeting requests" },
      { key: "unique_converters", label: "Unique converters" },
      { key: "conversion_rate", label: "Conversion rate", suffix: "%" },
      { key: "conversations_started", label: "Conversations started" },
      { key: "unique_conversation_starters", label: "Unique conversation starters" },
    ],
  },
  {
    id: "contact",
    title: "Contact & outbound actions",
    description: "An aggregate count of clicks on this profile's website, email, phone and social "
      + "links. The endpoint reports one total, so no per-link split is shown — and no address, "
      + "number, URL, handle or visitor identity is ever recorded or displayed.",
    metrics: [
      { key: "outbound_clicks", label: "Outbound link clicks" },
    ],
  },
  {
    id: "internal",
    title: "Team & internal navigation",
    description: "An aggregate count of clicks from this profile through to team members, events and "
      + "resources. The endpoint reports one total, so no per-destination split, destination id or "
      + "visitor identity is shown.",
    metrics: [
      { key: "internal_clicks", label: "Internal link clicks" },
    ],
  },
  {
    id: "engagement",
    title: "Chatter & engagement",
    metrics: [
      { key: "engagement_actions", label: "Engagement actions" },
      { key: "unique_engagers", label: "Unique engagers" },
      { key: "comments", label: "Comments" },
      { key: "reactions", label: "Reactions" },
      { key: "rechats", label: "Re-chats" },
      { key: "post_views", label: "Chatter post views" },
    ],
  },
];
